/**
 * @module experience/shared-browse
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

/* tslint:disable max-file-line-count */
// Core Angular
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TmoButton } from '@support/shared/widgets/interfaces/button.interface';
import { Wove } from 'aspect.js/';
import { throwError as observableThrowError, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

// Interfaces and Models
import { BrowseEntry } from '@experience/shop/shared-browse/interfaces/browse-entry.interface';
import { BrowseEntryConfigData } from '@core/aem/interfaces/common/browse-entry.interface';
import { EntryModalInterface } from '@experience/shop/shared-browse/interfaces/entry-modal.interface';
import { ZipCodeEntryChangeInterface } from '@experience/shop/shared-browse/interfaces/zip-code-entry-change.interface';
import { PlanTypeEntryChangeInterface } from '@experience/shop/shared-browse/interfaces/plan-type-entry-change.interface';
import { CreditEntryChangeInterface } from '@experience/shop/shared-browse/interfaces/credit-entry-change.interface';
import { APIError } from '@support/api/common/error.interface';
import { EstimatedCreditCheck } from '@support/api/proxies/responses/api-2033.interface';

// Services
import { SharedBrowseService } from '@experience/shop/shared-browse/services/shared-browse.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { SETTINGS } from '@environment/settings';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { MarketingService } from '@root/shared/services/marketing.service';

// contants
import {
  /* CHANNEL_CONST, */ SHARED_BROWSE_COMPONENTS,
} from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { BillingTypeEnum } from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { MaxLinesData } from '@support/api/proxies/responses/api-cart-response.interface';
import { EntryModalConstants } from '@experience/shop/shared-browse/components/entry-modal/entry-modal.constants';
import { ZipCodeRequiredGuardService } from '@experience/shop/services/zip-code-required-guard.service';
import { Address } from '@support/api/proxies/responses/api-2004.interface';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { WorkflowService } from '@root/shared/services/workflow.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { AppConfig } from '@root/app.config';

@Wove({ moduleName: SHARED_BROWSE_COMPONENTS.SHARED_BROWSE_MODULE })
@Component({
  selector: 'tmo-entry-modal',
  templateUrl: './entry-modal.component.html',
  styleUrls: ['../../../../../../style-sheets/components/modal.component.scss'],
})
export class EntryModalComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  /**
   * Entrymodal input interface
   */
  @Input() public modal: EntryModalInterface;
  /** Event emitted when button is clicked
   * It emits the 'Event' object.
   */
  @Output() public onSelect: EventEmitter<Object>;
  /**
   * Boolean holds spinner
   */
  public showSpinner: boolean;
  /**
   * Configurable AEM data
   */
  public entryModalConfigurableData: BrowseEntryConfigData;
  /**
   * FormGroup for entry modal component data
   */
  public entryModalForm: FormGroup;
  /**
   * isAssistedChannel checks if it is not a web channel
   */
  // public isAssistedChannel?: boolean;
  /**
   * Boolean to show and hide the modal .
   */
  public displayModal: boolean;

  public displayZipModal: boolean;
  /**
   * Used to store selected plan type .
   */
  public selectedPlanType: string;
  /**
   * Used to show credit status .
   */
  public showCreditStatus: boolean;
  /**
   * Object of type Browse entry used to store user state
   */
  public customerConfigData: BrowseEntry;
  /**
   * Used to store zipcode value to enable disable continue button
   */
  public changedZipCode: boolean;
  /**
   * Used to check accept .
   */
  private accept: boolean;

  /**
   * Subject to manage subscriptions
   */
  private ngUnsubscribe: Subject<void>;
  /**
   * Output to parent component
   */
  @Output() private onClose: EventEmitter<Object>;
  /**
   * Holds billing type coming from config file
   */
  public billingType: string;
  /**
   * Holds entry modal constants
   */
  public entryModalConstants: EntryModalInterface;
  /**
   * Stores entered address
   */
  public selectedAddress: Address;
  /**
   * Button data for continue CTA
   */
  public continueButton: TmoButton;
  /**
   * Stores Zipcode Cache Expiry Hours
   */
  private zipCodeExpiryHrs: number;

  /**
   * Constructor function for static modal.
   */
  constructor(
    private zipCodeRequiredGuardService: ZipCodeRequiredGuardService,
    private sharedBrowseService: SharedBrowseService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private commonUtilService: CommonUtilService,
    private objectService: ObjectService,
    private workflowService: WorkflowService,
    private router: Router,
    activatedRoute: ActivatedRoute,
    private marketingService: MarketingService,
    private cartSupportService: CartSupportService,
    pdlService: PdlService,
    private appConfig: AppConfig
  ) {
    super(pdlService, activatedRoute);
    this.accept = null;
    this.showCreditStatus = true;
    // this.isAssistedChannel = false;
    this.changedZipCode = false;
    this.onClose = new EventEmitter();
    this.onSelect = new EventEmitter();
    this.ngUnsubscribe = new Subject<void>();
  }
  /**
   * this method is executed on load.
   * @returns void
   */
  public ngOnInit(): void {
    super.ngOnInit();
    this.entryModalConstants = EntryModalConstants;
    this.sharedBrowseService.zipCodeNotAvailableSource
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((zipEntered: boolean) => {
        this.displayModal = zipEntered;
        if (this.displayModal === true) {
          const nextUrl = this.stateStoreUtilsService.getNextUrl();
          this.workflowService.setWorkflowIdOnRouteChange(nextUrl);
          this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'modalLoad');
        }
        this.selectedAddress = null;
        const billingType =
          SETTINGS.BILLINGTYPE !== '' ? SETTINGS.BILLINGTYPE : this.entryModalConstants.POSTPAID;
        this.showCreditStatus = billingType === this.entryModalConstants.POSTPAID;
        this.changedZipCode = false;
        this.entryModalForm = this.sharedBrowseService.buildEntryModalForm();
      });
    // this.isAssistedChannel = SETTINGS.CHANNEL.toLocaleLowerCase() !== CHANNEL_CONST.web;
    this.entryModalForm = this.sharedBrowseService.buildEntryModalForm();
    this.getConfigurableData();
    this.customerConfigData = this.sharedBrowseService.getUserInputsConfigData();
    if (!this.commonUtilService.isNotEmpty(this.customerConfigData)) {
      this.customerConfigData = this.instantiateConfigData();
      const billingType: string =
        this.customerConfigData.billingType === 0
          ? this.entryModalConstants.PREPAID
          : this.entryModalConstants.POSTPAID;
      this.stateStoreUtilsService.setBillingTypeOfCustomerInFocus(billingType);
    }
    this.sharedBrowseService.zipModalNotAvailableSource
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((ZipModal: boolean) => {
        this.handleDefaultSubmit();
      });
  }
  /**
   * Event handler for valid zip code change
   * @returns void
   */
  public zipCodeChanged(data: ZipCodeEntryChangeInterface): void {
    const formName = 'zipCodeValidationForm';
    this.entryModalForm.controls[formName] = data.formGroup;
    if (data && data.zipCodeData) {
      const address = {
        postalCode: data.zipCodeData,
        addressid: data.addressId,
        stateCode: data.stateCode,
        cityName: data.cityName,
      };
      this.changedZipCode = true;
      this.selectedAddress = address;
    } else {
      this.changedZipCode = false;
    }
  }

  /**
   * Event handler for plan-type change
   * @returns void
   */
  public planTypeChanged(data: PlanTypeEntryChangeInterface): void {
    this.showCreditStatus = data.showCreditStatus;
    this.customerConfigData.billingType = data.selectedPlanType;
    this.stateStoreUtilsService.setBillingTypeOfCustomerInFocus(
      BillingTypeEnum[this.customerConfigData.billingType]
    );
  }
  /**
   * Event handler for valid credit level change
   * @returns void
   */
  public creditLevelChanged(data: CreditEntryChangeInterface): void {
    this.customerConfigData.creditLevel = data.selectedCreditLevel;
  }

  /**
   * this method is executed when modal gets closed.
   *  @returns void
   */
  public closeDialog(): void {
    this.displayModal = false;
    this.sharedBrowseService.updateBrowseZipCode(this.selectedAddress);
  }
  /**
   * Emits an event which is used to execute
   * functionality on a particular button click.
   * @param event contains information on which button user has clicked
   */

  public handleSubmit(event?: EventEmitter<Object>): void {
    this.showSpinner = true;
    const radix = 10;
    let configData = this.sharedBrowseService.getUserInputsConfigData();
    if (!this.commonUtilService.isNotEmpty(configData)) {
      configData = this.instantiateConfigData();
    }
    configData.billingType = this.objectService.get(this.customerConfigData, 'billingType');
    this.sharedBrowseService.setUserInputsConfigData(configData);
    this.customerConfigData = this.sharedBrowseService.getUserInputsConfigData();
    if (!this.commonUtilService.isNotEmpty(this.customerConfigData)) {
      this.customerConfigData = this.instantiateConfigData();
    }
    const custData = this.customerConfigData;
    this.sharedBrowseService.setUserInputsConfigData(custData);
    const maxLinesData: MaxLinesData = {};
    if (custData.billingType === BillingTypeEnum.POSTPAID) {
      this.sharedBrowseService
        .postReferenceDetails(custData.creditLevel)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((response: EstimatedCreditCheck) => {
          // maxLinesData.maxVoiceLines = parseInt(this.objectService.get(response,
          //   'referenceData.0.linesWithoutDeposit'), radix);
          // @TODO need to remove this logic got multiline
          maxLinesData.maxVoiceLines = 1;
          this.closeModal(maxLinesData, event);
        });
    } else {
      const prepaidMaxLine = '5';
      maxLinesData.maxVoiceLines = parseInt(prepaidMaxLine, radix);
      this.closeModal(maxLinesData, event);
    }
  }

  /**
   * this method is used to set previous route in customerConfigData.
   */
  public setPreviousRoute(): void {
    this.stateStoreUtilsService.setPreviousUrl(this.router.url);
    this.sharedBrowseService.setUserInputsConfigData(this.customerConfigData);
  }
  /**
   * Destroys existing subscriptions.
   * @returns void
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * function to redirect the app to home page when modal is closed
   * @params showEntryModal: boolean
   * @returns: void
   */
  public closeEntryModal(showEntryModal?: boolean): void {
    this.displayModal = false;
    if (showEntryModal) {
      this.marketingService.updateMarketingInformationSubject(true);
    }
  }

  /**
   * Create instance of browse entry to feed child data into
   * @returns BrowseEntry
   */
  private instantiateConfigData(): BrowseEntry {
    const billingType = this.objectService.get(SETTINGS, 'BILLINGTYPE')
      ? SETTINGS.BILLINGTYPE.toLocaleLowerCase()
      : '';
    let billingTypeEnum;
    switch (billingType) {
      case this.entryModalConstants.PREPAID:
        this.billingType = this.entryModalConstants.PREPAID;
        billingTypeEnum = 0;
        break;
      case this.entryModalConstants.POSTPAID:
        this.billingType = this.entryModalConstants.POSTPAID;
        billingTypeEnum = 1;
        break;
      default:
        this.billingType = null;
    }
    return {
      zip: '',
      addresses: {},
      creditLevel: 0,
      billingType: billingTypeEnum,
      personalContactNumber: '',
    };
  }
  /**
   * Calls service to get configurable data for component and children
   * @returns void
   */
  private getConfigurableData(): void {
    this.sharedBrowseService
      .getBrowseEntryAEMContent()
      .pipe(
        catchError((err: APIError) => {
          return observableThrowError(err);
        }),
        takeUntil(this.ngUnsubscribe.asObservable())
      )
      .subscribe((aemData: BrowseEntryConfigData) => {
        this.entryModalConfigurableData = aemData;
        this.continueButton = {
          name: this.entryModalConfigurableData.continue,
          id: 'entry-modal-continue-cta',
          class: 'btn btn-primary',
          type: 'submit',
        };
      });
  }

  private closeModal(maxLinesData: MaxLinesData, event: EventEmitter<Object>): void {
    const nextUrl = this.stateStoreUtilsService.getNextUrl() || '/';
    this.stateStoreUtilsService.setMaxLineDetails(maxLinesData);
    this.onSelect.emit(event);
    this.showSpinner = false;
    if (this.changedZipCode) {
      this.sharedBrowseService.enableSetStoredZipCode();
    }
    // if (nextUrl === ROUTE_CONSTANTS.BROWSE_PLANS_ADD_FAN
    //   && SETTINGS.CHANNEL.toLocaleLowerCase() === CHANNEL_CONST.retail) {
    //   const shipTo = this.objectService.get(this.stateStoreUtilsService.getShipFulfillment(),
    //     'shipTo');
    //   this.router.navigateByUrl('device-setup');
    // } else {
    this.router.navigateByUrl(nextUrl);
    // }
    this.closeDialog();
  }

  public handleDefaultSubmit(): void {
    this.showSpinner = true;
    let configData = this.sharedBrowseService.getUserInputsConfigData();
    if (!this.commonUtilService.isNotEmpty(configData)) {
      configData = this.instantiateConfigData();
    }
    configData.billingType = this.objectService.get(this.customerConfigData, 'billingType');
    this.sharedBrowseService.setUserInputsConfigData(configData);
    this.customerConfigData = this.sharedBrowseService.getUserInputsConfigData();
    if (!this.commonUtilService.isNotEmpty(this.customerConfigData)) {
      this.customerConfigData = this.instantiateConfigData();
    }
    const custData = this.customerConfigData;
    this.sharedBrowseService.setUserInputsConfigData(custData);
    this.showSpinner = false;
  }
}
